import { useCurrentUser } from 'client/lib/auth';
import { SummarizeAvailableTimes } from './availability-summary';
import { RuzcalMgmtPage } from './mgmt-page';
import {
  ListAddAction,
  ListContainer,
  PageContent,
  PageHeading,
  PageSection,
  defCalRoute,
} from './common';
import { rpx } from 'client/lib/rpx-client';
import dayjs from 'dayjs';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { IcoClock, IcoPlus, IcoTrash } from '@components/icons';
import { useState } from 'preact/hooks';
import { BtnCancel, DialogHeader, showDialog, StandardDialog } from '@components/dialog';
import { showError } from '@components/app-error';
import { LoadedProps } from '@components/router';
import { isAllDay } from './dateutil';
import { showModalForm } from '@components/modal-form';
import { AvailabilityRow, EventTypeRow } from 'server/types/cal-schema';
import { summarizeDuration } from './cal-event-types-list';
import { showToast } from '@components/toaster';

type Props = LoadedProps<typeof load>;

type Data = Props['state'];

export const route = defCalRoute({ authLevel: 'guide', load, Page });

async function load() {
  const [availability, overrides] = await Promise.all([
    rpx.ruzcal.getAvailabilityList(),
    rpx.ruzcal.getOverridesList(),
  ]);
  return { availability, overrides };
}

async function deleteAvailableTimeSlot({
  toDeleteAvailableTime,
  toReplaceAvailableTime,
  onDelete,
}: {
  toDeleteAvailableTime: UUID;
  toReplaceAvailableTime?: UUID;
  onDelete: () => void;
}) {
  try {
    // Only do replacement if a slot is provided
    if (toReplaceAvailableTime) {
      await rpx.ruzcal.replaceEventTypesAvailableSlot({
        seekId: toDeleteAvailableTime,
        replaceId: toReplaceAvailableTime,
      });
    }

    await rpx.ruzcal.removeAvailabilityTime({ id: toDeleteAvailableTime });

    showToast({
      title: 'Availability slot deleted',
      type: 'ok',
      message: 'Your availability time slot has been deleted.',
    });

    onDelete();
  } catch (err) {
    showError(err);
  }
}

function ReplaceAvailabilityModal({
  availabilityId,
  availableTimes,
  close,
  eventTypes,
  onDelete,
}: {
  close: () => void;
  onDelete: () => void;
  eventTypes: EventTypeRow[];
  availabilityId: UUID;
  availableTimes: AvailabilityRow[];
}) {
  const user = useCurrentUser()!;
  const [selectedId, setSelectedId] = useState<UUID | undefined>(undefined);

  return (
    <StandardDialog onClose={close} contentWidth>
      <DialogHeader
        title="Replace Availability"
        subtitle="This availability slot is currently being used by the following event types: "
      />

      <section class="flex flex-col sm:gap-6 gap-10 sm:w-2xl lg:w-4xl max-w-screen overflow-auto">
        <ListContainer>
          {eventTypes.map((event) => (
            <div key={event.id} class="rounded-md flex justify-between items-start">
              <span class="flex-grow overflow-hidden">
                <span class="flex flex-col justify-between">{event.name}</span>

                <footer class="grid grid-cols-2 gap-2 sm:flex sm:gap-8 items-center">
                  <span class="inline-flex items-center gap-2">
                    <IcoClock /> {summarizeDuration(event.duration)}
                  </span>
                </footer>
              </span>
            </div>
          ))}
        </ListContainer>

        <p>
          Please select another availability slot where these event types should be moved before
          deleting this one.
        </p>

        <div class="flex flex-col border rounded-lg overflow-hidden divide-y">
          {availableTimes
            .filter((at) => at.id !== availabilityId)
            .map((p) => {
              const isSelected = selectedId === p.id;
              return (
                <Button
                  key={p.id}
                  onClick={() => {
                    setSelectedId(p.id);
                  }}
                  class={`flex flex-col text-sm text-left p-4 cursor-pointer transition-colors ${
                    isSelected ? 'bg-gray-100 border-l-4 border-indigo-500' : 'hover:bg-gray-100'
                  }`}
                >
                  <span>
                    {p.name}
                    <SummarizeAvailableTimes
                      renderTimeZone={user.timezone}
                      scheduleTimeZone={user.timezone}
                      timeslots={p.timeslots}
                    />
                  </span>
                </Button>
              );
            })}
        </div>
      </section>
      <footer class="flex gap-4 pt-3">
        <BtnPrimary
          class="rounded-full"
          onClick={async () => {
            const confirmed = await showDialog({
              mode: 'warn',
              title: 'Delete availability slot',
              children:
                'Are you sure you want to delete the availability slot and move the event types to the selected available time?',
              confirmButtonText: 'Delete and replace availabily',
            });

            if (confirmed) {
              deleteAvailableTimeSlot({
                toDeleteAvailableTime: availabilityId,
                toReplaceAvailableTime: selectedId,
                onDelete: onDelete,
              });
              close();
            }
          }}
          disabled={!selectedId}
        >
          Replace availability
        </BtnPrimary>

        <BtnCancel onClick={close}>Cancel</BtnCancel>
      </footer>
    </StandardDialog>
  );
}

async function handleAvailabilityBlockDelete({
  availability,
  onAvailabilityDelete,
  allAvailability,
}: {
  availability: AvailabilityRow;
  onAvailabilityDelete: () => void;
  allAvailability: AvailabilityRow[];
}) {
  // Check if availability block is linked to an event type
  try {
    const eventTypes = await rpx.ruzcal.getEventTypesUsingAvailableTimeId({
      id: availability.id,
    });

    if (eventTypes.length) {
      // Show Replace Availability Modal
      showModalForm(({ resolve }) => (
        <ReplaceAvailabilityModal
          close={resolve}
          onDelete={onAvailabilityDelete}
          eventTypes={eventTypes}
          availabilityId={availability.id}
          availableTimes={allAvailability}
        />
      ));
    } else {
      // Availability is not used by any event types, display delete warning.
      const confirmed = await showDialog({
        mode: 'warn',
        title: 'Delete availability slot',
        children:
          'Are you sure you want to delete this availability slot? This action cannot be undone.',
        confirmButtonText: 'Delete',
      });

      if (confirmed) {
        await deleteAvailableTimeSlot({
          toDeleteAvailableTime: availability.id,
          onDelete: onAvailabilityDelete,
        });
      }
    }
  } catch (err) {
    showError(err);
  }
}

type EditAvailabilityLinkProps = {
  availability: AvailabilityRow;
  showDelete: boolean;
  onDelete: () => void;
};

function EditAvailabilityLink({ availability, showDelete, onDelete }: EditAvailabilityLinkProps) {
  const user = useCurrentUser()!;

  return (
    <div class="flex items-center justify-between hover:bg-gray-100 transition-all rounded-md">
      <a
        href={`/calendar/availability/${availability.id}`}
        class="block p-3 hover:bg-gray-100 transition-all text-inherit rounded-md flex-grow"
      >
        <span class="flex flex-col justify-between">
          <span class="font-semibold">{availability.name}</span>
          <SummarizeAvailableTimes
            renderTimeZone={user.timezone}
            scheduleTimeZone={availability.timezone}
            timeslots={availability.timeslots}
          />
        </span>
      </a>
      {showDelete && (
        <Button
          onClick={onDelete}
          title="Delete availability slot"
          class="hover:bg-red-600 text-gray-500 hover:text-white p-3 rounded-md transition-all"
        >
          <IcoTrash class="size-4" />
        </Button>
      )}
    </div>
  );
}

function OverrideItem({
  override,
  onDelete,
}: {
  override: Data['overrides'][0];
  onDelete: () => void;
}) {
  const user = useCurrentUser()!;

  async function handleDelete() {
    const confirmed = await showDialog({
      mode: 'warn',
      title: 'Delete override',
      children: 'Are you sure you want to delete this override? This action cannot be undone.',
      confirmButtonText: 'Delete',
    });

    if (!confirmed) {
      return;
    }

    try {
      await rpx.ruzcal.deleteOverride({ id: override.id });
      onDelete();
    } catch (err) {
      showError(err);
    }
  }

  const allDay = isAllDay(override);

  return (
    <div class="flex items-center justify-between hover:bg-gray-100 transition-all rounded-md">
      <a href={`/calendar/overrides/${override.id}`} class="text-inherit grow p-3 flex">
        <span>
          {dayjs(override.start)
            .tz(user.timezone)
            .format(allDay ? 'MMM DD, YYYY' : 'MMM DD, YYYY h:mma')}
        </span>
        {' - '}
        <span>
          {dayjs(override.end)
            .tz(user.timezone)
            .format(allDay ? 'MMM DD, YYYY' : 'MMM DD, YYYY h:mma')}
        </span>
        {allDay && ' (all day)'}
      </a>
      <Button
        onClick={handleDelete}
        title="Delete override"
        class="hover:bg-red-600 text-gray-500 hover:text-white p-3 rounded-md transition-all"
      >
        <IcoTrash class="size-4" />
      </Button>
    </div>
  );
}

function Page(props: Props) {
  const [overrides, setOverrides] = useState(props.state.overrides);
  const [availability, setAvailability] = useState(props.state.availability);

  function handleDeleteAvailabilitySlot(avail: AvailabilityRow) {
    handleAvailabilityBlockDelete({
      availability: avail,
      allAvailability: availability,
      onAvailabilityDelete: () => {
        setAvailability((prev) => prev.filter((x) => x.id !== avail.id));
      },
    });
  }

  const canDeleteAvailability = availability.length > 1;

  return (
    <RuzcalMgmtPage
      title="Availability"
      currentPage="availability"
      actions={
        <>
          <ListAddAction href="/calendar/availability/new" content="Add availability" />
        </>
      }
    >
      <PageContent>
        <PageSection>
          <PageHeading
            title="Manage your availability"
            subtitle="Define blocks of time when you're available for booking."
          />
          {availability.length > 0 && (
            <ListContainer>
              {availability.map((avail) => (
                <EditAvailabilityLink
                  key={avail.id}
                  availability={avail}
                  showDelete={canDeleteAvailability}
                  onDelete={() => handleDeleteAvailabilitySlot(avail)}
                />
              ))}
            </ListContainer>
          )}
        </PageSection>
        <PageSection>
          <PageHeading
            title="Date overrides"
            subtitle="Remove specific dates and times from your public availability."
            action={
              <BtnSecondary href="/calendar/overrides/new">
                <IcoPlus class="w-4 h-4 mr-2" />
                Add override
              </BtnSecondary>
            }
          />
          {overrides.length > 0 && (
            <ListContainer>
              {overrides.map((x) => (
                <OverrideItem
                  key={x.id}
                  override={x}
                  onDelete={() => setOverrides(overrides.filter((o) => o.id !== x.id))}
                />
              ))}
            </ListContainer>
          )}
        </PageSection>
      </PageContent>
    </RuzcalMgmtPage>
  );
}
